import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import './ApplicationsList.css'; // reuse same table styles
import AdminLayout from './AdminLayout';

const PORTAL = '/ft-portal';

const StudentRequests = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalInstructors, setModalInstructors] = useState([]);
  const [modalInstructorsLoading, setModalInstructorsLoading] = useState(false);
  const [assigningId, setAssigningId] = useState(null);
  const [approving, setApproving] = useState(null);
  const [assignInstructorId, setAssignInstructorId] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsRequest, setDetailsRequest] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectingName, setRejectingName] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundTarget, setRefundTarget] = useState(null);
  const [refundNote, setRefundNote] = useState('');
  const [refundSending, setRefundSending] = useState(false);
  const [refundResult, setRefundResult] = useState(null);
  const [resendingPayEmail, setResendingPayEmail] = useState(null); // id being resent

  // ── External Instructor Assignment ──────────────────────────────
  const [assignTab, setAssignTab] = useState('registered'); // 'registered' | 'external'
  const [externalForm, setExternalForm] = useState({
    name: '', phone: '', email: '',
    car_make: '', car_model: '', car_year: '', car_colour: '', car_registration: ''
  });
  const [externalAssigning, setExternalAssigning] = useState(false);

  // ── Manual Payment Modal ──────────────────────────────────────
  const [showManualPayModal, setShowManualPayModal] = useState(false);
  const [manualPayTarget, setManualPayTarget] = useState(null); // student request obj
  const [manualPayForm, setManualPayForm] = useState({});
  const [manualPayLoading, setManualPayLoading] = useState(false);
  const [manualPayResult, setManualPayResult] = useState(null);

  const handleOpenManualPay = (req) => {
    setManualPayTarget(req);
    setManualPayForm({
      full_name: req.full_name || '',
      email: req.email || '',
      phone: req.phone || '',
      package_type: req.package_type || '',
      test_centre: req.test_centre || '',
      transmission: req.transmission || '',
      amount_pounds: '',
      payment_method: 'bank_transfer',
      notes: 'Manual override — student paid outside of Stripe',
    });
    setManualPayResult(null);
    setShowManualPayModal(true);
  };

  const handleManualPaySubmit = async () => {
    if (!manualPayForm.amount_pounds || isNaN(parseFloat(manualPayForm.amount_pounds))) {
      alert('Please enter a valid amount.');
      return;
    }
    setManualPayLoading(true);
    setManualPayResult(null);
    try {
      // 1) Record payment — pass the student_request_id so the correct row is used
      const payRes = await api.post('/admin/payments/manual', {
        ...manualPayForm,
        student_request_id: manualPayTarget.id,
      });
      const bookingRef = payRes?.bookingRef || '';
      await fetchRequests();
      setManualPayResult({
        success: true,
        message: `Payment recorded & ${manualPayTarget.full_name} approved. Confirmation emails sent.`,
        bookingRef,
      });
    } catch (err) {
      setManualPayResult({ success: false, message: err.response?.data?.message || err.response?.data?.error || 'Failed to record payment.' });
    } finally {
      setManualPayLoading(false);
    }
  };

  const statusOptions = [
    { value: 'all', label: 'All Active' },
    { value: 'pending', label: 'Pending' },
    { value: 'assigned', label: 'Assigned' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  useEffect(() => {
    fetchRequests();
  }, []);

  useEffect(() => {
    let data = [...requests];
    // When showing all, exclude approved/completed — those students are in the Students tab
    if (statusFilter === 'all') {
      data = data.filter(r => r.status !== 'approved' && r.status !== 'completed');
    } else {
      data = data.filter(r => r.status === statusFilter);
    }
    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      data = data.filter(r =>
        r.full_name?.toLowerCase().includes(t) ||
        r.email?.toLowerCase().includes(t) ||
        r.test_centre?.toLowerCase().includes(t)
      );
    }
    setFiltered(data);
  }, [requests, searchTerm, statusFilter]);

  const openRefundModal = (req) => {
    setRefundTarget({
      full_name: req.full_name,
      email: req.email,
      booking_ref: req.payment_intent_id || '',
      package_type: req.package_type || '',
    });
    setRefundNote('');
    setRefundResult(null);
    setShowRefundModal(true);
  };

  const closeRefundModal = () => {
    setShowRefundModal(false);
    setRefundTarget(null);
    setRefundResult(null);
    setRefundNote('');
  };

  const sendRefundEmail = async () => {
    if (!refundTarget) return;
    setRefundSending(true);
    setRefundResult(null);
    try {
      await api.post('/admin/refunds/send-email', {
        email: refundTarget.email,
        full_name: refundTarget.full_name,
        booking_ref: refundTarget.booking_ref,
        package_type: refundTarget.package_type,
        admin_note: refundNote,
      });
      setRefundResult({ success: true, message: `Refund email sent to ${refundTarget.email}` });
    } catch (err) {
      setRefundResult({ success: false, message: err.response?.data?.message || 'Failed to send email. Please try again.' });
    } finally {
      setRefundSending(false);
    }
  };

  const openAssignModal = async (req) => {
    setSelected(req);
    setAssignInstructorId(req.assigned_instructor_id || '');
    setAssignTab('registered');
    setExternalForm({ name: '', phone: '', email: '', car_make: '', car_model: '', car_year: '', car_colour: '', car_registration: '' });
    setShowModal(true);
    setModalInstructors([]);
    setModalInstructorsLoading(true);
    try {
      const tc = req.test_centre || '';
      const tr = (req.transmission || '').toLowerCase();
      const params = new URLSearchParams();
      if (tc) params.append('testCentre', tc);
      // Only filter by transmission if student has a specific preference (not 'either')
      if (tr && tr !== 'either') params.append('transmission', tr);
      const url = `/admin/instructors-list${params.toString() ? '?' + params.toString() : ''}`;
      const data = await api.get(url);
      setModalInstructors(Array.isArray(data) ? data : []);
    } catch { setModalInstructors([]); }
    finally { setModalInstructorsLoading(false); }
  };

  const closeModal = () => { setShowModal(false); setSelected(null); setModalInstructors([]); };

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const data = await api.get('/admin/student-requests');
      setRequests(Array.isArray(data) ? data : data.requests || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching student requests:', err);
      setError('Failed to load student requests.');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (instructorId) => {
    if (!instructorId || !selected) return;
    const ins = modalInstructors.find(i => String(i.id) === String(instructorId));
    const name = ins?.first_name || 'selected instructor';
    if (!window.confirm(`Assign ${name} to ${selected.full_name}?`)) return;
    try {
      setAssigningId(String(instructorId));
      await api.put(`/admin/student-requests/${selected.id}/assign`, { instructor_id: instructorId });
      await fetchRequests();
      closeModal();
    } catch {
      alert('Failed to assign instructor');
    } finally {
      setAssigningId(null);
    }
  };

  const handleAssignExternal = async () => {
    if (!selected) return;
    const { name, phone } = externalForm;
    if (!name.trim() || !phone.trim()) {
      alert('Instructor name and phone are required.');
      return;
    }
    if (!window.confirm(`Assign external instructor "${name.trim()}" to ${selected.full_name}?\n\nA payment email will be sent to the student.`)) return;
    try {
      setExternalAssigning(true);
      await api.put(`/admin/student-requests/${selected.id}/assign-external`, externalForm);
      await fetchRequests();
      closeModal();
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to assign external instructor');
    } finally {
      setExternalAssigning(false);
    }
  };

  // Manual approve — ONLY use when student has paid by other means (bank transfer, cash, etc.)
  // In the normal flow, approval is automatic via the Stripe webhook after payment.
  const handleManualApprove = async (id) => {
    const target = requests.find(r => r.id === id);
    // Allow approval if there's a registered instructor OR external instructor data
    const hasExternalInstructor = !!target?.external_instructor_data;
    if (!target?.assigned_instructor_id && !hasExternalInstructor) {
      alert('Cannot approve: no instructor has been assigned yet.\nPlease use the "Assign" button to assign an instructor first.');
      return;
    }
    if (!window.confirm(
      '⚠️ MANUAL APPROVAL — BYPASS PAYMENT\n\n' +
      'This should only be used if the student has paid by other means (bank transfer, cash, etc.).\n\n' +
      'In the normal flow, approval happens automatically when the student pays via the link in their email.\n\n' +
      'Are you sure you want to manually approve this student without Stripe payment confirmation?'
    )) return;
    try {
      setApproving(id);
      await api.post(`/admin/student-requests/${id}/approve`, {});
      await fetchRequests();
    } catch {
      alert('Failed to approve student');
    } finally {
      setApproving(null);
    }
  };

  // Resend the "confirm & pay" email to an assigned student
  const handleResendPayEmail = async (req) => {
    if (!window.confirm(`Re-send the payment confirmation email to ${req.email}?`)) return;
    setResendingPayEmail(req.id);
    try {
      await api.put(`/admin/student-requests/${req.id}/assign`, {
        instructor_id: req.assigned_instructor_id || req.instructor_id,
      });
      alert(`✅ Payment email re-sent to ${req.email}`);
    } catch {
      alert('Failed to re-send email. Please try again.');
    } finally {
      setResendingPayEmail(null);
    }
  };

  const handleStatusUpdate = async (id, newStatus, rejection_reason = '') => {
    try {
      await api.put(`/admin/student-requests/${id}/status`, { status: newStatus, rejection_reason });
      await fetchRequests();
    } catch {
      alert('Failed to update status');
    }
  };

  const handleDeleteRequest = async (id, name) => {
    if (!window.confirm(`Permanently delete the cancelled request for "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/student-requests/${id}`);
      await fetchRequests();
    } catch {
      alert('Failed to delete request');
    }
  };

  const openDetails = (req) => { setDetailsRequest(req); setShowDetailsModal(true); };
  const closeDetails = () => { setShowDetailsModal(false); setDetailsRequest(null); };

  const openRejectModal = (id, name) => { setRejectingId(id); setRejectingName(name); setRejectionReason(''); setShowRejectModal(true); };
  const closeRejectModal = () => { setShowRejectModal(false); setRejectingId(null); setRejectionReason(''); };
  const confirmReject = async () => {
    if (!rejectionReason.trim()) { alert('Please enter a rejection reason — it will be emailed to the student.'); return; }
    await handleStatusUpdate(rejectingId, 'cancelled', rejectionReason);
    closeRejectModal();
    if (detailsRequest?.id === rejectingId) closeDetails();
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  if (loading) {
    return (
      <div className="applications-list">
        <div className="loading-container">
          <div className="spinner-large"></div>
          <p>Loading student requests...</p>
        </div>
      </div>
    );
  }

  return (
    <AdminLayout pageTitle="Student Requests">
      <div className="applications-list">
        <div className="list-header">
          <h1>🎓 Student Requests</h1>
        </div>

        {error && <div className="alert alert-error">⚠️ {error}</div>}

        <div className="list-controls">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by name, email, or area..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="status-filter"
          >
            {statusOptions.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <div className="results-count">
            {filtered.length} of {requests.length} requests
          </div>
          <button className="ap-refresh-btn" onClick={fetchRequests} disabled={loading} title="Refresh">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ transition: 'transform 0.4s', transform: loading ? 'rotate(360deg)' : 'none' }}>
              <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        <div className="table-container" style={{ overflowX: 'auto' }}>
          <table className="applications-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Student</th>
                <th>Area</th>
                <th>Transmission</th>
                <th>Package</th>
                <th>Instructor</th>
                <th>Status</th>
                <th>Requested</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length > 0 ? (
                filtered.map((req) => (
                  <tr key={req.id}>
                    <td>#{req.id}</td>
                    <td>
                      <strong>{req.full_name}</strong>
                      <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{req.email}</div>
                      {req.phone && <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{req.phone}</div>}
                    </td>
                    <td>{req.test_centre || '—'}</td>
                    <td>{req.transmission || '—'}</td>
                    <td>{req.package_type || '—'}</td>
                    <td>
                      {(() => {
                        // Check for external instructor (not registered on platform)
                        let extInstr = null;
                        try { extInstr = req.external_instructor_data ? JSON.parse(req.external_instructor_data) : null; } catch (_) { }
                        if (extInstr?.name) {
                          return <span style={{ color: '#7c3aed', fontWeight: 600 }} title="External instructor (not registered on platform)">{extInstr.name}</span>;
                        }
                        return req.instructor_name
                          ? <span style={{ color: '#16a34a', fontWeight: 600 }}>{req.instructor_name}</span>
                          : <span style={{ color: '#f59e0b', fontWeight: 600 }}>Unassigned</span>;
                      })()}
                    </td>
                    <td>
                      <span className={`status-badge ${req.status}`}>
                        {req.status?.replace('_', ' ')}
                      </span>
                    </td>
                    <td>{req.created_at ? formatDate(req.created_at) : '—'}</td>
                    <td style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', minWidth: '160px', alignItems: 'center' }}>
                      <button onClick={() => openDetails(req)} className="btn-view">View</button>

                      {/* Assign button — show for pending & assigned (can re-assign) */}
                      {req.status !== 'approved' && req.status !== 'completed' && req.status !== 'cancelled' && (
                        <button onClick={() => openAssignModal(req)} className="btn-view">
                          {req.status === 'assigned' ? '↻ Re-assign' : 'Assign'}
                        </button>
                      )}

                      {/* ASSIGNED: awaiting payment — show resend email + manual override */}
                      {req.status === 'assigned' && (
                        <>
                          <span title="Payment email sent — waiting for student to pay" style={{
                            fontSize: '0.72rem', fontWeight: 700, color: '#b45309',
                            background: '#fef3c7', border: '1px solid #fde68a',
                            borderRadius: 5, padding: '2px 7px', whiteSpace: 'nowrap',
                          }}>
                            ⏳ Awaiting Payment
                          </span>
                          <button
                            onClick={() => handleResendPayEmail(req)}
                            disabled={resendingPayEmail === req.id}
                            title="Re-send the pay-now email to this student"
                            style={{ background: '#eff6ff', color: '#1d4ed8', border: '1.5px solid #bfdbfe', borderRadius: 6, padding: '4px 9px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
                          >
                            {resendingPayEmail === req.id ? '⏳' : '📧 Resend Link'}
                          </button>
                          <button
                            onClick={() => handleOpenManualPay(req)}
                            title="Record a manual payment (cash / bank transfer) and approve this student"
                            style={{ background: '#f8fafc', color: '#64748b', border: '1px dashed #cbd5e1', borderRadius: 6, padding: '3px 8px', fontSize: '0.72rem', cursor: 'pointer', whiteSpace: 'nowrap' }}
                          >
                            ⚠ Manual
                          </button>
                        </>
                      )}

                      {/* PENDING: just needs an instructor assigned */}
                      {req.status === 'pending' && (
                        <span title="Assign an instructor first — then the student will receive a payment link" style={{
                          fontSize: '0.72rem', color: '#94a3b8', fontStyle: 'italic',
                        }}>
                          Awaiting instructor
                        </span>
                      )}

                      {/* APPROVED: payment confirmed by Stripe */}
                      {req.status === 'approved' && (
                        <span style={{
                          fontSize: '0.72rem', fontWeight: 700, color: '#166534',
                          background: '#dcfce7', border: '1px solid #bbf7d0',
                          borderRadius: 5, padding: '2px 7px', whiteSpace: 'nowrap',
                        }}>
                          ✔ Paid &amp; Confirmed
                        </span>
                      )}

                      {/* Reject button for pending / assigned */}
                      {(req.status === 'pending' || req.status === 'assigned') && (
                        <button onClick={() => openRejectModal(req.id, req.full_name)} className="btn-reject">
                          ✖ Reject
                        </button>
                      )}

                      {req.status === 'cancelled' && (
                        <>
                          <button onClick={() => handleStatusUpdate(req.id, 'pending')} className="btn-back" title="Re-open this request">
                            ↩ Re-open
                          </button>
                          <button
                            onClick={() => handleDeleteRequest(req.id, req.full_name || `${req.first_name || ''} ${req.last_name || ''}`.trim())}
                            className="btn-reject"
                            title="Permanently delete this cancelled request"
                          >
                            🗑 Delete
                          </button>
                        </>
                      )}
                      {!['pending', 'assigned', 'approved', 'completed', 'cancelled'].includes(req.status) && (
                        <button onClick={() => handleStatusUpdate(req.id, 'pending')} className="btn-back" title={`Status is '${req.status || 'unknown'}' — reset to Pending`}>
                          ↩ Reset
                        </button>
                      )}
                      {/* Initiate Refund — available for assigned/approved/completed requests */}
                      {/* {['assigned', 'approved', 'completed', 'pending'].includes(req.status) && (
                      <button
                        onClick={() => openRefundModal(req)}
                        style={{ background: '#fff7ed', color: '#9a3412', border: '1.5px solid #fed7aa', borderRadius: 6, padding: '4px 10px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
                        title="Send student a refund initiation email"
                      >
                        💷 Refund
                      </button>
                    )} */}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="no-data">
                    {error ? 'Error loading data.' : 'No student requests found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Assign Instructor Modal */}
        {showModal && selected && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '620px', width: '95%' }}>
              <div className="modal-header">
                <h2>Assign Instructor</h2>
                <button className="modal-close" onClick={closeModal}>×</button>
              </div>
              <div className="modal-body">
                {/* Student summary */}
                <div className="sr-info-grid" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', fontSize: '0.875rem' }}>
                  <div><span style={{ color: '#64748b' }}>Student: </span><strong>{selected.full_name}</strong></div>
                  <div><span style={{ color: '#64748b' }}>Area: </span><strong>{selected.test_centre || 'Not specified'}</strong></div>
                  <div><span style={{ color: '#64748b' }}>Transmission: </span><strong>{selected.transmission || '—'}</strong></div>
                  <div><span style={{ color: '#64748b' }}>Package: </span><strong>{selected.package_type || '—'}</strong></div>
                </div>

                {/* Tab Toggle */}
                <div style={{ display: 'flex', gap: 0, marginBottom: '16px', borderRadius: 8, overflow: 'hidden', border: '1.5px solid #e2e8f0' }}>
                  <button
                    onClick={() => setAssignTab('registered')}
                    style={{
                      flex: 1, padding: '9px 0', border: 'none', cursor: 'pointer', fontWeight: 700,
                      fontSize: '0.85rem', borderRight: '1px solid #e2e8f0',
                      background: assignTab === 'registered' ? '#0A1F4E' : '#f8fafc',
                      color: assignTab === 'registered' ? '#fff' : '#475569',
                      transition: 'background 0.15s, color 0.15s'
                    }}
                  >
                    📋 Registered Instructor
                  </button>
                  <button
                    onClick={() => setAssignTab('external')}
                    style={{
                      flex: 1, padding: '9px 0', border: 'none', cursor: 'pointer', fontWeight: 700,
                      fontSize: '0.85rem',
                      background: assignTab === 'external' ? '#7c3aed' : '#f8fafc',
                      color: assignTab === 'external' ? '#fff' : '#475569',
                      transition: 'background 0.15s, color 0.15s'
                    }}
                  >
                    External Instructor
                  </button>
                </div>

                {/* ─── TAB: REGISTERED INSTRUCTORS ─── */}
                {assignTab === 'registered' && (
                  <>
                    {selected?.transmission?.toLowerCase() === 'automatic' && (
                      <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '8px 14px', marginBottom: 12, fontSize: '0.82rem', color: '#166534', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>🚗</span>
                        <span>Student requires <strong>Automatic</strong> transmission — only showing matching instructors.</span>
                      </div>
                    )}

                    <div style={{ marginBottom: '12px', fontWeight: 700, fontSize: '0.9rem', color: '#0f172a' }}>
                      Available Instructors {selected.test_centre ? `— ${selected.test_centre}` : ''}
                    </div>

                    {modalInstructorsLoading ? (
                      <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                        <div className="spinner-large" /><p>Loading instructors…</p>
                      </div>
                    ) : modalInstructors.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '1.5rem', color: '#94a3b8', background: '#f8fafc', borderRadius: '8px', marginBottom: '16px' }}>
                        <p style={{ margin: 0 }}>No instructors found for this area.</p>
                        <p style={{ margin: '4px 0 0', fontSize: '0.8rem' }}>Switch to the <strong>External Instructor</strong> tab to assign a non-registered instructor.</p>
                      </div>
                    ) : null}

                    {(() => {
                      const wantsAuto = selected?.transmission?.toLowerCase() === 'automatic';
                      const filteredInstructors = wantsAuto
                        ? modalInstructors.filter(i => i.transmission_type?.toLowerCase() === 'automatic')
                        : modalInstructors;
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '300px', overflowY: 'auto', marginBottom: '16px' }}>
                          {filteredInstructors.length === 0 && !modalInstructorsLoading ? (
                            <div style={{ textAlign: 'center', padding: '1.5rem', color: '#94a3b8', background: '#f8fafc', borderRadius: 8 }}>
                              {wantsAuto ? 'No instructors with automatic transmission found.' : 'No instructors found.'}
                            </div>
                          ) : filteredInstructors.map(ins => (
                            <div
                              key={ins.id}
                              className="sr-instructor-card"
                              style={{
                                border: assignInstructorId === String(ins.id) ? '2px solid #003399' : '1px solid #e2e8f0',
                                borderRadius: '10px', padding: '12px 16px',
                                background: assignInstructorId === String(ins.id) ? '#f0f4ff' : '#fff',
                                display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer',
                              }}
                              onClick={() => setAssignInstructorId(String(ins.id))}
                            >
                              <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#003399', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '1rem', flexShrink: 0 }}>
                                {(ins.first_name || '?').split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase()}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f172a' }}>{ins.first_name}</div>
                                <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 2, display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                  {ins.phone && <a href={`tel:${ins.phone}`} onClick={e => e.stopPropagation()} style={{ color: '#003399', textDecoration: 'none', fontWeight: 600 }}>📞 {ins.phone}</a>}
                                  {ins.email && <a href={`mailto:${ins.email}`} onClick={e => e.stopPropagation()} style={{ color: '#003399', textDecoration: 'none' }}>✉️ {ins.email}</a>}
                                </div>
                                <div style={{ marginTop: 5, display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                                  {ins.transmission_type && (
                                    <span style={{ background: ins.transmission_type.toLowerCase() === 'automatic' ? '#dcfce7' : '#f0f9ff', color: ins.transmission_type.toLowerCase() === 'automatic' ? '#166534' : '#1e40af', padding: '2px 8px', borderRadius: 12, fontWeight: 700, fontSize: '0.72rem', textTransform: 'capitalize' }}>
                                      {ins.transmission_type}
                                    </span>
                                  )}
                                  {ins.vehicle_make && (
                                    <span style={{ fontSize: '0.76rem', color: '#64748b' }}>
                                      🚗 {ins.vehicle_make}{ins.vehicle_model ? ` ${ins.vehicle_model}` : ''}{ins.vehicle_year ? ` (${ins.vehicle_year})` : ''}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <button
                                className="btn-approve"
                                style={{ flexShrink: 0 }}
                                disabled={!!assigningId}
                                onClick={(e) => { e.stopPropagation(); handleAssign(ins.id); }}
                                type="button"
                              >
                                {assigningId === String(ins.id) ? 'Assigning…' : 'Assign'}
                              </button>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </>
                )}

                {/* ─── TAB: EXTERNAL INSTRUCTOR ─── */}
                {assignTab === 'external' && (
                  <div>
                    <div style={{ background: '#faf5ff', border: '1.5px solid #e9d5ff', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: '0.82rem', color: '#6b21a8' }}>
                      Enter the details of an instructor <strong>not registered on the platform</strong>. A payment email will be sent to the student using this instructor's name.
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      {[{ key: 'name', label: 'Full Name *', placeholder: 'e.g. James Wilson', full: true },
                      { key: 'phone', label: 'Phone *', placeholder: '07700 900000', full: true },
                      { key: 'email', label: 'Email', placeholder: 'james@example.com', full: true },
                      { key: 'car_make', label: 'Car Make', placeholder: 'e.g. Toyota' },
                      { key: 'car_model', label: 'Car Model', placeholder: 'e.g. Corolla' },
                      { key: 'car_year', label: 'Car Year', placeholder: 'e.g. 2021' },
                      { key: 'car_colour', label: 'Car Colour', placeholder: 'e.g. Silver' },
                      { key: 'car_registration', label: 'Registration', placeholder: 'e.g. AB21 XYZ' },
                      ].map(f => (
                        <div key={f.key} style={f.full ? { gridColumn: '1 / -1' } : {}}>
                          <label style={{ display: 'block', fontWeight: 600, fontSize: '0.8rem', color: '#475569', marginBottom: 4 }}>{f.label}</label>
                          <input
                            type="text"
                            value={externalForm[f.key]}
                            onChange={e => setExternalForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                            placeholder={f.placeholder}
                            style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: '0.875rem', boxSizing: 'border-box' }}
                          />
                        </div>
                      ))}
                    </div>
                    <button
                      className="btn-approve"
                      onClick={handleAssignExternal}
                      disabled={externalAssigning}
                      style={{ marginTop: 20, width: '100%', padding: '11px 0', fontSize: '0.95rem' }}
                    >
                      {externalAssigning ? 'Assigning…' : 'Assign External Instructor'}
                    </button>
                  </div>
                )}

                <div className="modal-actions">
                  <button onClick={closeModal} className="btn-back">Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ─── Student Details Modal ─── */}
        {showDetailsModal && detailsRequest && (
          <div className="modal-overlay" onClick={closeDetails}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '620px', width: '95%' }}>
              <div className="modal-header">
                <h2>Student Details — #{detailsRequest.id}</h2>
                <button className="modal-close" onClick={closeDetails}>×</button>
              </div>
              <div className="modal-body">
                {/* Info grid */}
                <div className="sr-info-grid" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '14px 16px', marginBottom: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', fontSize: '0.875rem' }}>
                  <div><span style={{ color: '#64748b' }}>Name: </span><strong>{detailsRequest.full_name}</strong></div>
                  <div><span style={{ color: '#64748b' }}>Email: </span><strong>{detailsRequest.email}</strong></div>
                  <div><span style={{ color: '#64748b' }}>Phone: </span><strong>{detailsRequest.phone || '—'}</strong></div>
                  <div><span style={{ color: '#64748b' }}>Area: </span><strong>{detailsRequest.test_centre || '—'}</strong></div>
                  <div><span style={{ color: '#64748b' }}>Transmission: </span><strong>{detailsRequest.transmission || '—'}</strong></div>
                  <div><span style={{ color: '#64748b' }}>Package: </span><strong>{detailsRequest.package_type || '—'}</strong></div>
                  <div><span style={{ color: '#64748b' }}>Status: </span><span className={`status-badge ${detailsRequest.status}`} style={{ fontSize: '0.8rem' }}>{detailsRequest.status}</span></div>
                  <div><span style={{ color: '#64748b' }}>Submitted: </span><strong>{detailsRequest.created_at ? formatDate(detailsRequest.created_at) : '—'}</strong></div>
                  {detailsRequest.message && (
                    <div style={{ gridColumn: '1 / -1' }}><span style={{ color: '#64748b' }}>Message: </span><span>{detailsRequest.message}</span></div>
                  )}
                </div>

                {/* Driving licence photos */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f172a', marginBottom: '12px' }}>🪪 Driving Licence</div>
                  {(detailsRequest.driving_licence_front || detailsRequest.driving_licence_back) ? (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      {detailsRequest.driving_licence_front && (
                        <div>
                          <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '6px', fontWeight: 600 }}>Front</div>
                          <a href={`/api/admin/view-file?path=${encodeURIComponent(detailsRequest.driving_licence_front)}`} target="_blank" rel="noopener noreferrer">
                            <img
                              src={`/api/admin/view-file?path=${encodeURIComponent(detailsRequest.driving_licence_front)}`}
                              alt="Licence Front"
                              style={{ width: '100%', borderRadius: '6px', border: '1px solid #e2e8f0', objectFit: 'cover', maxHeight: '160px', cursor: 'pointer' }}
                              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                            />
                            <div style={{ display: 'none', padding: '12px', background: '#f8fafc', borderRadius: '6px', fontSize: '0.8rem', color: '#64748b', textAlign: 'center' }}>
                              📄 View file
                            </div>
                          </a>
                        </div>
                      )}
                      {detailsRequest.driving_licence_back && (
                        <div>
                          <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '6px', fontWeight: 600 }}>Back</div>
                          <a href={`/api/admin/view-file?path=${encodeURIComponent(detailsRequest.driving_licence_back)}`} target="_blank" rel="noopener noreferrer">
                            <img
                              src={`/api/admin/view-file?path=${encodeURIComponent(detailsRequest.driving_licence_back)}`}
                              alt="Licence Back"
                              style={{ width: '100%', borderRadius: '6px', border: '1px solid #e2e8f0', objectFit: 'cover', maxHeight: '160px', cursor: 'pointer' }}
                              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                            />
                            <div style={{ display: 'none', padding: '12px', background: '#f8fafc', borderRadius: '6px', fontSize: '0.8rem', color: '#64748b', textAlign: 'center' }}>
                              📄 View file
                            </div>
                          </a>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '8px', color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center' }}>
                      No licence photos uploaded yet.
                    </div>
                  )}
                </div>

                {/* Previous rejection reason (if any) */}
                {detailsRequest.rejection_reason && (
                  <div style={{ background: '#fff3f3', border: '1px solid #fca5a5', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', fontSize: '0.875rem' }}>
                    <div style={{ fontWeight: 700, color: '#CF142B', marginBottom: '4px' }}>Previous Rejection Reason:</div>
                    <div style={{ color: '#7f1d1d' }}>{detailsRequest.rejection_reason}</div>
                  </div>
                )}

                <div className="modal-actions">
                  <button onClick={closeDetails} className="btn-back">Close</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ─── Refund Initiation Modal ───
      {showRefundModal && refundTarget && (
        <div className="modal-overlay" onClick={closeRefundModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', width: '95%' }}>
            <div className="modal-header" style={{ borderBottom: '3px solid #f59e0b' }}>
              <h2>💷 Initiate Refund for Student</h2>
              <button className="modal-close" onClick={closeRefundModal}>×</button>
            </div>
            <div className="modal-body">
              {refundResult ? (
                <div style={{ textAlign: 'center', padding: '16px 0' }}>
                  <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>{refundResult.success ? '✅' : '❌'}</div>
                  <p style={{ fontWeight: 700, fontSize: '1rem', color: refundResult.success ? '#166534' : '#7f1d1d', margin: '0 0 8px' }}>
                    {refundResult.success ? 'Email Sent!' : 'Failed to Send'}
                  </p>
                  <p style={{ fontSize: '0.875rem', color: '#64748b', margin: '0 0 20px' }}>{refundResult.message}</p>
                  <button onClick={refundResult.success ? closeRefundModal : () => setRefundResult(null)} className={refundResult.success ? 'btn-approve' : 'btn-back'}>
                    {refundResult.success ? 'Done' : 'Try Again'}
                  </button>
                </div>
              ) : (
                <>
                  <p style={{ fontSize: '0.9rem', color: '#475569', margin: '0 0 20px', lineHeight: 1.6 }}>
                    This will send an email to the student informing them their instructor is unavailable
                    and prompting them to submit a refund request.
                  </p>
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '14px 16px', marginBottom: '20px', fontSize: '0.875rem' }}>
                    <div style={{ marginBottom: '6px' }}><strong>Student:</strong> {refundTarget.full_name}</div>
                    <div style={{ marginBottom: '6px' }}><strong>Email:</strong> {refundTarget.email}</div>
                    {refundTarget.package_type && <div style={{ marginBottom: '6px' }}><strong>Package:</strong> {refundTarget.package_type}</div>}
                    {refundTarget.booking_ref && <div><strong>Booking Ref:</strong> <span style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>{refundTarget.booking_ref}</span></div>}
                  </div>
                  <div className="form-group">
                    <label style={{ fontWeight: 600, display: 'block', marginBottom: 6, fontSize: '0.875rem' }}>
                      Additional Note to Student <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span>
                    </label>
                    <textarea
                      value={refundNote}
                      onChange={(e) => setRefundNote(e.target.value)}
                      placeholder="e.g. We sincerely apologise for the inconvenience. Your refund will be processed within 3–5 working days once received."
                      className="admin-notes"
                      rows="3"
                      style={{ marginTop: 0 }}
                    />
                  </div>
                  <div className="modal-actions">
                    <button onClick={closeRefundModal} className="btn-back">Cancel</button>
                    <button onClick={sendRefundEmail} className="btn-approve" disabled={refundSending}>
                      {refundSending ? '⏳ Sending…' : '📧 Send Refund Email'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )} */}

        {/* ─── Manual Payment Modal ─── */}
        {showManualPayModal && manualPayTarget && (
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
            onClick={() => { if (!manualPayLoading) { setShowManualPayModal(false); setManualPayTarget(null); } }}
          >
            <div
              style={{ background: '#fff', borderRadius: 14, maxWidth: 580, width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#0A1F4E' }}>💳 Record Manual Payment</h2>
                <button onClick={() => { setShowManualPayModal(false); setManualPayTarget(null); }} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}>×</button>
              </div>

              <div style={{ padding: '20px 24px' }}>
                {manualPayResult ? (
                  <div style={{ textAlign: 'center', padding: '24px 0' }}>
                    <div style={{ fontSize: '3rem', marginBottom: 14 }}>{manualPayResult.success ? '✅' : '❌'}</div>
                    <p style={{ fontWeight: 700, fontSize: '1.05rem', color: manualPayResult.success ? '#166534' : '#7f1d1d', margin: '0 0 8px' }}>
                      {manualPayResult.success ? 'Payment Recorded!' : 'Error'}
                    </p>
                    <p style={{ fontSize: '0.875rem', color: '#64748b', margin: '0 0 12px' }}>{manualPayResult.message}</p>
                    {manualPayResult.bookingRef && (
                      <p style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '1.1rem', color: '#1043A0', margin: '0 0 24px', letterSpacing: '0.05em' }}>
                        Booking Ref: {manualPayResult.bookingRef}
                      </p>
                    )}
                    <button onClick={() => { setShowManualPayModal(false); setManualPayTarget(null); }} style={{ padding: '10px 28px', background: '#1043A0', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>Close</button>
                  </div>
                ) : (
                  <>
                    {/* Info banner */}
                    <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '10px 14px', marginBottom: 20, fontSize: 13, color: '#0369a1', lineHeight: 1.6 }}>
                      ℹ️ Use this when a student paid by <strong>bank transfer, cash or cheque</strong> outside of Stripe. A payment record will be created, confirmation emails sent, and the student approved.
                    </div>

                    {/* Student Details */}
                    <p style={{ margin: '0 0 10px', fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Student Details</p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }}>
                      {[['full_name', 'Full Name', 'text'], ['email', 'Email', 'email'], ['phone', 'Phone', 'text']].map(([field, label, type]) => (
                        <div key={field} style={field === 'full_name' ? { gridColumn: '1 / -1' } : {}}>
                          <label style={{ display: 'block', marginBottom: 5, fontWeight: 600, fontSize: 12, color: '#475569' }}>{label}</label>
                          <input
                            type={type}
                            value={manualPayForm[field] || ''}
                            onChange={e => setManualPayForm(f => ({ ...f, [field]: e.target.value }))}
                            style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none' }}
                          />
                        </div>
                      ))}
                    </div>

                    {/* Booking Details */}
                    <p style={{ margin: '0 0 10px', fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Booking Details</p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: 5, fontWeight: 600, fontSize: 12, color: '#475569' }}>Package</label>
                        <input
                          type="text"
                          value={manualPayForm.package_type || ''}
                          readOnly
                          style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', background: '#f8fafc', color: '#475569', outline: 'none' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: 5, fontWeight: 600, fontSize: 12, color: '#475569' }}>Transmission</label>
                        <select
                          value={manualPayForm.transmission || ''}
                          onChange={e => setManualPayForm(f => ({ ...f, transmission: e.target.value }))}
                          style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                        >
                          <option value="">Select...</option>
                          <option value="Manual">Manual</option>
                          <option value="Automatic">Automatic</option>
                          <option value="Either">Either</option>
                        </select>
                      </div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ display: 'block', marginBottom: 5, fontWeight: 600, fontSize: 12, color: '#475569' }}>Test Centre</label>
                        <input
                          type="text"
                          value={manualPayForm.test_centre || ''}
                          onChange={e => setManualPayForm(f => ({ ...f, test_centre: e.target.value }))}
                          style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none' }}
                        />
                      </div>
                    </div>

                    {/* Payment Details */}
                    <p style={{ margin: '0 0 10px', fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Payment Details</p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: 5, fontWeight: 600, fontSize: 12, color: '#475569' }}>Amount Paid (£) <span style={{ color: '#CF142B' }}>*</span></label>
                        <input
                          type="number"
                          placeholder="e.g. 70"
                          min="0"
                          step="0.01"
                          value={manualPayForm.amount_pounds || ''}
                          onChange={e => setManualPayForm(f => ({ ...f, amount_pounds: e.target.value }))}
                          style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #1043A0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none' }}
                          autoFocus
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: 5, fontWeight: 600, fontSize: 12, color: '#475569' }}>Payment Method</label>
                        <select
                          value={manualPayForm.payment_method || 'bank_transfer'}
                          onChange={e => setManualPayForm(f => ({ ...f, payment_method: e.target.value }))}
                          style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                        >
                          <option value="cash">💵 Cash</option>
                          <option value="bank_transfer">🏦 Bank Transfer</option>
                          <option value="card">💳 Card (manual)</option>
                          <option value="cheque">📄 Cheque</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ display: 'block', marginBottom: 5, fontWeight: 600, fontSize: 12, color: '#475569' }}>Notes</label>
                        <textarea
                          value={manualPayForm.notes || ''}
                          onChange={e => setManualPayForm(f => ({ ...f, notes: e.target.value }))}
                          rows={2}
                          style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
                        />
                      </div>
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', borderTop: '1px solid #f1f5f9', paddingTop: 16, marginTop: 4 }}>
                      <button onClick={() => { setShowManualPayModal(false); setManualPayTarget(null); }} disabled={manualPayLoading} style={{ padding: '10px 20px', border: '1.5px solid #e2e8f0', borderRadius: 8, background: '#fff', color: '#475569', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>Cancel</button>
                      <button onClick={handleManualPaySubmit} disabled={manualPayLoading} style={{ padding: '10px 24px', background: manualPayLoading ? '#94a3b8' : '#1043A0', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: manualPayLoading ? 'default' : 'pointer', fontSize: 14 }}>
                        {manualPayLoading ? '⏳ Processing…' : '💳 Record Payment & Approve'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ─── Rejection Reason Modal ─── */}
        {showRejectModal && (
          <div className="modal-overlay" onClick={closeRejectModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px', width: '95%' }}>
              <div className="modal-header" style={{ borderBottom: '3px solid #CF142B' }}>
                <h2>Reject Student Request</h2>
                <button className="modal-close" onClick={closeRejectModal}>×</button>
              </div>
              <div className="modal-body">
                <p style={{ margin: '0 0 16px', fontSize: '0.9rem', color: '#475569' }}>
                  Rejecting <strong>{rejectingName}</strong>. Please provide a reason — this will be emailed to the student.
                </p>
                <div className="form-group">
                  <label style={{ fontWeight: 600, color: '#CF142B' }}>Rejection Reason <span style={{ fontWeight: 400, fontSize: '0.82em', color: '#64748b' }}>(required — emailed to student)</span></label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="e.g. Driving licence could not be verified. Please resubmit with a clearer photo..."
                    className="admin-notes"
                    rows="4"
                    style={{ borderColor: '#CF142B', marginTop: '6px' }}
                    autoFocus
                  />
                </div>
                <div className="modal-actions">
                  <button onClick={closeRejectModal} className="btn-back">Cancel</button>
                  <button onClick={confirmReject} className="btn-reject">✖ Confirm Rejection</button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>    </AdminLayout>);
};

export default StudentRequests;
